/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/require-default-props */
import React from 'react';
import { Flex, InputGroup, Input, InputProps, FormControl, Button, FormLabel, Box, Text, HStack, Stack } from '@chakra-ui/core';
import { MdRefresh } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import colors from '../../../styles/colors';
import { useStoreState } from '../../../models/hooks';

// Reusable SearchInput component
interface SearchInputProps extends Omit<InputProps, 'size'> {
  'data-testid': string;
  onEnter: () => void;
  flexConfig: Record<string, string | number>;
  label: string;
  hasError?: boolean;
}

const CustomSearchInput: React.FC<SearchInputProps> = ({
  'data-testid': dataTestId,
  onEnter,
  flexConfig,
  label,
  hasError = false,
  ...inputProps
}) => (
  <FormControl flex={flexConfig} mr={2} isInvalid={hasError}>
    <FormLabel htmlFor="label" mb="0" fontSize="sm">
      {label}
    </FormLabel>

    <InputGroup size="md">
      <Input
        id={label}
        data-testid={dataTestId}
        variant="outline"
        size="sm"
        borderRadius="md"
        {...inputProps}
        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
          /* istanbul ignore else */
          if (e.key === 'Enter') {
            onEnter();
          }
          inputProps.onKeyPress?.(e);
        }}
        _placeholder={{ color: colors.gray[400], fontSize: '14px' }}
      />
    </InputGroup>
  </FormControl>
);

export interface SearchCriteria {
  candidateName: string;
  phone: string;
  email: string;
  hireId: string;
  requisitionId: string;
}

interface CandidateSearchProps {
  search: SearchCriteria;
  setSearch: React.Dispatch<React.SetStateAction<SearchCriteria>>;
  onReset?: () => void;
  isFetching?: boolean;
}
const defaultSearch = {
  candidateName: '',
  phone: '',
  email: '',
  hireId: '',
  requisitionId: '',
};
export const CandidateSearch = React.memo(function CandidateSearch({
  search,
  setSearch,
  onReset,
  isFetching,
}: CandidateSearchProps) {
  const { t } = useTranslation('candidateSearch');
  const isHireFormEnabled = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabled);
  const isRequisitionEnabled = useStoreState((s) => s.app.accounts?.configuration?.requisitionEnabled);

  const [error, setError] = React.useState<(keyof SearchCriteria)[] | null>(null);

  const [searchFilters, setSearchFilters] = React.useState(search || /* istanbul ignore next */ defaultSearch);
  React.useEffect(() => {
    setSearchFilters(search || /* istanbul ignore next */ defaultSearch);
    setError(null);
  }, [search]);

  const handleCandidateSearch = React.useCallback(() => {
    const entries = Object.entries(searchFilters) as [keyof typeof searchFilters, string][];

    // Get all filled fields
    const filledFields = entries.filter(([_, value]) => value.length > 0);

    if (filledFields.length === 0) {
      // No fields filled → do nothing / clear search results

      /* istanbul ignore else */
      if (onReset) {
        onReset();
      }
      setError(null);
      return;
    }

    // Fields that are valid (length >= 4)
    const validFields = filledFields.filter(([_, value]) => value.length >= 4);

    if (validFields.length > 0) {
      // At least one valid field → search passes
      setError(null);
      setSearch(searchFilters);
    } else {
      // All filled fields invalid → set error on first failing field
      const failingFields = filledFields.map(([key]) => key);
      setError(failingFields);
    }
  }, [searchFilters, setSearch, setError, onReset]);

  const clearSearch = React.useCallback(() => {
    setSearchFilters(defaultSearch);
    /* istanbul ignore else */
    if (onReset) {
      onReset();
      setError(null);
    }
  }, [onReset]);

  const handleInputChange = React.useCallback((field: keyof typeof searchFilters, value: string) => {
    setSearchFilters((prev) => {
      const newFilters = { ...prev, [field]: value };

      return newFilters;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack mb={2}>
      <HStack justify="space-between">
        <Box>
          <Text fontSize="lg" fontWeight="bold">
            {t('filters.title')}
          </Text>
        </Box>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearSearch}
          aria-label="Clear Search"
          leftIcon={<MdRefresh />}
          isDisabled={isFetching}
        >
          <Text fontWeight="normal">{t('filters.reset')}</Text>
        </Button>
      </HStack>
      <Flex justifyContent="space-between" flexWrap="wrap" width="100%">
        <CustomSearchInput
          label={t('filters.name.label')}
          data-testid="searchNameInput"
          placeholder={t('filters.name.placeholder')}
          value={searchFilters.candidateName}
          maxLength={100}
          flexConfig={{ base: '1 1 100%', md: '1 1 48%', lg: '1 1 19%' }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            // const trimmedName = e.target.value
            //   .replace(/[^a-zA-Z0-9\s.]/g, '')
            //   .replace(/\s+/g, ' ')
            //   .trim();
            handleInputChange('candidateName', e.target.value);
          }}
          onEnter={handleCandidateSearch}
          isDisabled={isFetching}
          hasError={error?.includes('candidateName')}
        />
        <CustomSearchInput
          label={t('filters.phone.label')}
          data-testid="searchPhoneInput"
          type="tel"
          placeholder={t('filters.phone.placeholder')}
          value={searchFilters.phone}
          maxLength={12}
          flexConfig={{ base: '1 1 100%', md: '1 1 48%', lg: '1 1 19%' }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const numericValue = e.target.value.replace(/\D/g, '');
            handleInputChange('phone', numericValue);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            /* istanbul ignore else */
            if (!/[0-9]/.test(e.key) && e.key.length === 1) {
              e.preventDefault();
            }
          }}
          onEnter={handleCandidateSearch}
          isDisabled={isFetching}
          hasError={error?.includes('phone')}
        />
        <CustomSearchInput
          label={t('filters.email.label')}
          data-testid="searchEmailInput"
          placeholder={t('filters.email.placeholder')}
          value={searchFilters.email}
          maxLength={100}
          flexConfig={{ base: '1 1 100%', md: '1 1 48%', lg: '1 1 19%' }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const trimmedEmail = e.target.value
              // .replace(/[^a-zA-Z0-9\s.@]/g, '')
              .replace(/\s+/g, ' ')
              .trim();
            handleInputChange('email', trimmedEmail);
          }}
          onEnter={handleCandidateSearch}
          isDisabled={isFetching}
          hasError={error?.includes('email')}
        />
        {isHireFormEnabled && (
          <CustomSearchInput
            label={t('filters.hireId.label')}
            data-testid="searchHireIDInput"
            placeholder={t('filters.hireId.placeholder')}
            value={searchFilters.hireId}
            maxLength={100}
            flexConfig={{ base: '1 1 100%', md: '1 1 48%', lg: '1 1 19%' }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('hireId', e.target.value)}
            onEnter={handleCandidateSearch}
            isDisabled={isFetching}
            hasError={error?.includes('hireId')}
          />
        )}
        {isRequisitionEnabled && (
          <CustomSearchInput
            label={t('filters.requistion.label')}
            data-testid="searchRequisitionIDInput"
            placeholder={t('filters.requistion.placeholder')}
            value={searchFilters.requisitionId}
            maxLength={100}
            flexConfig={{ base: '1 1 100%', md: '1 1 48%', lg: '1 1 19%' }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('requisitionId', e.target.value)}
            onEnter={handleCandidateSearch}
            isDisabled={isFetching}
            hasError={error?.includes('requisitionId')}
          />
        )}
      </Flex>
      {error && (
        <Text fontSize="sm" color={colors.red[500]}>
          {t('filters.info')}
        </Text>
      )}
    </Stack>
  );
});
